/**
 * Curated mock data for the public demo page.
 *
 * Unlike the dev mocks in mock-data.ts, these are hand-tuned fixed values
 * that tell a compelling product story: spend trending efficiently,
 * positive ROI, improving prompt scores, and actionable alerts.
 */

import type { DailySpend, ProviderUsageSummary } from '@/lib/mock-data';
import type { UsageEvent, Provider, PromptAnalysis, PromptScoreBreakdown } from '@/lib/types';

// ---------------------------------------------------------------------------
// Daily spend — 30 days, weekday-heavy, showing a slight optimization dip
// ---------------------------------------------------------------------------

const BASE_DATE = new Date('2026-01-23');

function daySpend(offset: number, oa: number, an: number, go: number): DailySpend {
  const d = new Date(BASE_DATE);
  d.setDate(d.getDate() + offset);
  return {
    date: d.toISOString().split('T')[0],
    openai: oa,
    anthropic: an,
    google: go,
    confirmed: { openai: oa, anthropic: an, google: go },
    estimated: { openai: 0, anthropic: 0, google: 0 },
  };
}

export const DEMO_DAILY_SPEND: DailySpend[] = [
  daySpend(0, 112, 58, 31),
  daySpend(1, 108, 62, 29),
  daySpend(2, 118, 55, 34),
  daySpend(3, 42, 18, 10),  // weekend
  daySpend(4, 38, 22, 12),  // weekend
  daySpend(5, 105, 60, 28),
  daySpend(6, 110, 57, 30),
  daySpend(7, 98, 52, 26),
  daySpend(8, 102, 54, 27),
  daySpend(9, 95, 48, 25),
  daySpend(10, 35, 15, 8),  // weekend
  daySpend(11, 32, 17, 9),  // weekend
  daySpend(12, 92, 50, 24),
  daySpend(13, 88, 46, 22),
  daySpend(14, 85, 44, 21),
  daySpend(15, 90, 47, 23),
  daySpend(16, 82, 42, 20),
  daySpend(17, 30, 14, 7),  // weekend
  daySpend(18, 28, 12, 6),  // weekend
  daySpend(19, 78, 40, 19),
  daySpend(20, 75, 38, 18),
  daySpend(21, 72, 36, 17),
  daySpend(22, 70, 35, 16),
  daySpend(23, 68, 34, 15),
  daySpend(24, 25, 11, 5),  // weekend
  daySpend(25, 22, 10, 4),  // weekend
  daySpend(26, 65, 32, 14),
  daySpend(27, 62, 30, 13),
  daySpend(28, 60, 28, 12),
  daySpend(29, 58, 27, 11),
];

// ---------------------------------------------------------------------------
// Provider usage summary — shows healthy multi-provider split
// ---------------------------------------------------------------------------

export const DEMO_PROVIDER_USAGE: ProviderUsageSummary[] = [
  { provider: 'anthropic', model: 'claude-opus-4.6', totalTokens: 8_420_000, totalCost: 1248.60, cacheReadTokens: 1_200_000, cacheWriteTokens: 340_000 },
  { provider: 'anthropic', model: 'claude-sonnet-4-20250514', totalTokens: 4_380_000, totalCost: 726.80, cacheReadTokens: 680_000, cacheWriteTokens: 120_000 },
  { provider: 'anthropic', model: 'claude-haiku-4-20250514', totalTokens: 5_610_000, totalCost: 278.40, cacheReadTokens: 0, cacheWriteTokens: 0 },
  { provider: 'openai', model: 'gpt-4o', totalTokens: 2_150_000, totalCost: 228.40, cacheReadTokens: 0, cacheWriteTokens: 0 },
  { provider: 'openai', model: 'gpt-4o-mini', totalTokens: 1_820_000, totalCost: 83.80, cacheReadTokens: 0, cacheWriteTokens: 0 },
  { provider: 'google', model: 'gemini-2.5-pro', totalTokens: 640_000, totalCost: 196.00, cacheReadTokens: 0, cacheWriteTokens: 0 },
];

// ---------------------------------------------------------------------------
// Recent events — 20 curated events with realistic tags
// ---------------------------------------------------------------------------

function evt(
  i: number,
  minsAgo: number,
  provider: Provider,
  model: string,
  promptTk: number,
  completionTk: number,
  cost: number,
  tags: Record<string, string>
): UsageEvent {
  const ts = new Date(Date.now() - minsAgo * 60_000);
  return {
    id: `evt_demo_${String(i).padStart(3, '0')}`,
    org_id: 'org_demo',
    user_id: null,
    provider,
    model,
    request_id: `req_d${String(i).padStart(4, '0')}`,
    prompt_tokens: promptTk,
    completion_tokens: completionTk,
    total_tokens: promptTk + completionTk,
    cache_read_tokens: null,
    cache_write_tokens: null,
    estimated_cost: cost,
    reconciled_cost: null,
    tags,
    metadata: {},
    prompt_storage_path: null,
    response_storage_path: null,
    source: 'sdk',
    session_id: null,
    created_at: ts.toISOString(),
  };
}

export const DEMO_RECENT_EVENTS: UsageEvent[] = [
  evt(1, 3, 'anthropic', 'claude-opus-4.6', 2800, 1200, 0.42, { team: 'engineering', feature: 'code-review' }),
  evt(2, 8, 'anthropic', 'claude-sonnet-4-20250514', 3200, 1800, 0.65, { team: 'product', feature: 'user-research' }),
  evt(3, 12, 'anthropic', 'claude-haiku-4-20250514', 1500, 800, 0.09, { team: 'support', feature: 'ticket-summary' }),
  evt(4, 18, 'google', 'gemini-2.5-pro', 900, 450, 0.09, { team: 'data', feature: 'analysis' }),
  evt(5, 22, 'anthropic', 'claude-opus-4.6', 4100, 2200, 0.72, { team: 'engineering', feature: 'test-generation' }),
  evt(6, 30, 'anthropic', 'claude-haiku-4-20250514', 600, 300, 0.02, { team: 'support', feature: 'ticket-summary' }),
  evt(7, 35, 'google', 'gemini-2.5-pro', 2400, 1600, 0.82, { team: 'product', feature: 'strategy-doc' }),
  evt(8, 42, 'anthropic', 'claude-opus-4.6', 1800, 900, 0.31, { team: 'marketing', feature: 'copy-writing' }),
  evt(9, 50, 'anthropic', 'claude-sonnet-4-20250514', 2600, 1400, 0.52, { team: 'engineering', feature: 'code-review' }),
  evt(10, 58, 'anthropic', 'claude-haiku-4-20250514', 800, 400, 0.05, { team: 'data', feature: 'classification' }),
  evt(11, 65, 'google', 'gemini-2.5-pro', 1100, 550, 0.12, { team: 'engineering', feature: 'docs-generation' }),
  evt(12, 72, 'anthropic', 'claude-sonnet-4-20250514', 3800, 2100, 0.78, { team: 'product', feature: 'competitive-analysis' }),
  evt(13, 80, 'anthropic', 'claude-opus-4.6', 2200, 1100, 0.38, { team: 'marketing', feature: 'email-campaign' }),
  evt(14, 88, 'anthropic', 'claude-haiku-4-20250514', 700, 350, 0.04, { team: 'support', feature: 'auto-reply' }),
  evt(15, 95, 'google', 'gemini-2.5-pro', 500, 250, 0.05, { team: 'data', feature: 'etl-pipeline' }),
  evt(16, 104, 'openai', 'gpt-4o', 2800, 1600, 0.19, { team: 'finance', feature: 'forecasting' }),
  evt(17, 112, 'openai', 'gpt-4o-mini', 1400, 900, 0.02, { team: 'support', feature: 'ticket-summary' }),
  evt(18, 121, 'openai', 'gpt-4o', 2600, 1500, 0.18, { team: 'engineering', feature: 'incident-postmortem' }),
  evt(19, 129, 'openai', 'gpt-4o-mini', 1200, 700, 0.02, { team: 'product', feature: 'roadmap-draft' }),
  evt(20, 138, 'openai', 'gpt-4o', 2400, 1300, 0.16, { team: 'finance', feature: 'budget-variance' }),
];

// ---------------------------------------------------------------------------
// ROI metrics — positive ROI story
// ---------------------------------------------------------------------------

export const DEMO_ROI = {
  efficiency: 0.0082,
  efficiencyTrend: 'down' as const,
  efficiencyTrendLabel: '-6.3% vs last week',
  totalCost: 1_042.80,
  costTrend: 'down' as const,
  costTrendLabel: '-14.2% vs last week',
  totalOutput: 12_450_000,
  outputTrend: 'up' as const,
  outputTrendLabel: '+8.1% vs last week',
};

export const DEMO_ROI_TIMELINE = [
  { week: 'Week 1', cost: 1480, hoursSaved: 42, roi: 118 },
  { week: 'Week 2', cost: 1320, hoursSaved: 48, roi: 145 },
  { week: 'Week 3', cost: 1180, hoursSaved: 52, roi: 176 },
  { week: 'Week 4', cost: 1042, hoursSaved: 58, roi: 210 },
];

export const DEMO_ROI_SUMMARY = {
  totalSpend: 5_022,
  totalHoursSaved: 200,
  costPerHour: 25.11,
  netValue: 9_978,
  roiPercent: 199,
};

// ---------------------------------------------------------------------------
// Prompt coaching scores — improving trend
// ---------------------------------------------------------------------------

export const DEMO_PROMPT_SCORES: PromptScoreBreakdown = {
  overall: 74,
  clarity: 78,
  specificity: 68,
  structure: 82,
  efficiency: 65,
  context: 76,
};

export const DEMO_PROMPT_TREND = [
  { period: 'Week 1', overall: 58, clarity: 62, specificity: 48, structure: 64, efficiency: 52, context: 60 },
  { period: 'Week 2', overall: 64, clarity: 68, specificity: 56, structure: 72, efficiency: 56, context: 66 },
  { period: 'Week 3', overall: 70, clarity: 74, specificity: 62, structure: 78, efficiency: 60, context: 72 },
  { period: 'Week 4', overall: 74, clarity: 78, specificity: 68, structure: 82, efficiency: 65, context: 76 },
];

export const DEMO_SAMPLE_ANALYSES: Pick<PromptAnalysis, 'id' | 'overall_score' | 'provider' | 'model' | 'issues' | 'strengths' | 'token_waste_estimate' | 'cost_waste_estimate' | 'improvement_tags' | 'prompt_category' | 'complexity'>[] = [
  {
    id: 'pa_demo_001',
    overall_score: 82,
    provider: 'anthropic',
    model: 'claude-opus-4.6',
    issues: [
      { type: 'missing_constraints', severity: 'medium', suggestion: 'Add output format and length constraints to reduce retries.' },
    ],
    strengths: [
      { type: 'clear_intent', note: 'The task description is well-defined and actionable.' },
      { type: 'good_context', note: 'Relevant background information provided.' },
    ],
    token_waste_estimate: 340,
    cost_waste_estimate: 0.05,
    improvement_tags: ['add_constraints'],
    prompt_category: 'code_generation',
    complexity: 'moderate',
  },
  {
    id: 'pa_demo_002',
    overall_score: 58,
    provider: 'anthropic',
    model: 'claude-sonnet-4-20250514',
    issues: [
      { type: 'vague_instruction', severity: 'high', excerpt: 'Write something about our product...', suggestion: 'Specify audience, tone, format, and key messages.' },
      { type: 'no_examples', severity: 'medium', suggestion: 'Include 1-2 examples of desired output style.' },
    ],
    strengths: [
      { type: 'topic_clarity', note: 'The subject matter is clearly identified.' },
    ],
    token_waste_estimate: 1_820,
    cost_waste_estimate: 0.34,
    improvement_tags: ['add_specificity', 'add_examples'],
    prompt_category: 'writing',
    complexity: 'simple',
  },
  {
    id: 'pa_demo_003',
    overall_score: 91,
    provider: 'anthropic',
    model: 'claude-opus-4.6',
    issues: [],
    strengths: [
      { type: 'structured_prompt', note: 'Well-organized with clear sections and formatting.' },
      { type: 'specific_constraints', note: 'Output format, length, and style clearly specified.' },
      { type: 'good_examples', note: 'Few-shot examples guide the model effectively.' },
    ],
    token_waste_estimate: 45,
    cost_waste_estimate: 0.007,
    improvement_tags: [],
    prompt_category: 'code_generation',
    complexity: 'complex',
  },
];

// ---------------------------------------------------------------------------
// Alerts & insights — sample triggered alerts
// ---------------------------------------------------------------------------

export const DEMO_ALERTS = [
  {
    id: 'alert_demo_001',
    title: 'Opus 4.6 spend spike detected',
    description: 'Engineering team usage 34% above daily average',
    type: 'usage_spike' as const,
    severity: 'warning' as const,
    time: '12 min ago',
    team: 'engineering',
  },
  {
    id: 'alert_demo_002',
    title: 'Monthly budget 80% consumed',
    description: '$4,018 of $5,000 budget used with 8 days remaining',
    type: 'budget_threshold' as const,
    severity: 'warning' as const,
    time: '2 hours ago',
    team: 'all',
  },
  {
    id: 'alert_demo_003',
    title: 'Billing reconciliation mismatch',
    description: 'OpenAI invoice $124 higher than tracked usage for Feb 1-15',
    type: 'reconciliation_discrepancy' as const,
    severity: 'error' as const,
    time: '1 day ago',
    team: 'finance',
  },
];

export const DEMO_WEEKLY_INSIGHT = {
  title: 'Weekly Optimization Report',
  highlights: [
    'Spend decreased 14.2% week-over-week through model right-sizing',
    'Prompt coaching improved avg score from 70 to 74 (+5.7%)',
    'Cache hit rate for Opus 4.6 reached 14.3%, saving an est. $178',
    'Engineering team adopted Claude Haiku 4 for simple tasks, saving $312',
  ],
};

// ---------------------------------------------------------------------------
// Demo conversation — a realistic prompt + response with coaching insights
// ---------------------------------------------------------------------------

export interface DemoMessage {
  id: string;
  role: 'human' | 'assistant';
  content: string;
  model: string | null;
  provider: 'anthropic';
  promptTokens: number | null;
  completionTokens: number | null;
  cost: number | null;
  timestamp: string;
}

export interface DemoConversation {
  id: string;
  title: string;
  model: string;
  provider: 'anthropic';
  tool: string;
  user: string;
  project: string;
  messageCount: number;
  totalTokens: number;
  totalCost: number;
  durationMinutes: number;
  startedAt: string;
  endedAt: string;
  messages: DemoMessage[];
  promptScore: number;
  coachingInsights: {
    overall: number;
    dimensions: { label: string; score: number; tip: string }[];
    suggestions: string[];
    strengths: string[];
    rewrite: string;
  };
}

const convBase = new Date('2026-02-20T14:22:00Z');

export const DEMO_CONVERSATION: DemoConversation = {
  id: 'demo_session_001',
  title: 'API Error Handling Refactor',
  model: 'claude-opus-4.6',
  provider: 'anthropic',
  tool: 'Cursor',
  user: 'sarah@acme.com',
  project: 'backend-api',
  messageCount: 4,
  totalTokens: 8_420,
  totalCost: 0.84,
  durationMinutes: 12,
  startedAt: convBase.toISOString(),
  endedAt: new Date(convBase.getTime() + 12 * 60_000).toISOString(),
  messages: [
    {
      id: 'msg_001',
      role: 'human',
      content: `Refactor our API error handling. We have a lot of try/catch blocks that are duplicated across routes. Make it better.`,
      model: null,
      provider: 'anthropic',
      promptTokens: 1_240,
      completionTokens: null,
      cost: null,
      timestamp: convBase.toISOString(),
    },
    {
      id: 'msg_002',
      role: 'assistant',
      content: `I'll help you refactor the error handling. Here's a centralized approach using middleware and custom error classes:

\`\`\`typescript
// lib/errors.ts
export class AppError extends Error {
  constructor(
    public statusCode: number,
    public code: string,
    message: string,
    public isOperational = true
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export class NotFoundError extends AppError {
  constructor(resource: string) {
    super(404, 'NOT_FOUND', \`\${resource} not found\`);
  }
}

export class ValidationError extends AppError {
  constructor(message: string) {
    super(400, 'VALIDATION_ERROR', message);
  }
}
\`\`\`

\`\`\`typescript
// middleware/error-handler.ts
export function withErrorHandler(handler: RouteHandler) {
  return async (req: Request, ctx: RouteContext) => {
    try {
      return await handler(req, ctx);
    } catch (error) {
      if (error instanceof AppError) {
        return Response.json(
          { error: { code: error.code, message: error.message } },
          { status: error.statusCode }
        );
      }
      console.error('Unhandled error:', error);
      return Response.json(
        { error: { code: 'INTERNAL', message: 'Something went wrong' } },
        { status: 500 }
      );
    }
  };
}
\`\`\`

Now your routes become much cleaner:

\`\`\`typescript
// Before (repeated across every route)
export async function GET(req: Request) {
  try {
    const user = await db.getUser(id);
    if (!user) return Response.json({ error: 'Not found' }, { status: 404 });
    return Response.json(user);
  } catch (e) {
    return Response.json({ error: 'Server error' }, { status: 500 });
  }
}

// After
export const GET = withErrorHandler(async (req, ctx) => {
  const user = await db.getUser(id);
  if (!user) throw new NotFoundError('User');
  return Response.json(user);
});
\`\`\`

This eliminates duplicated try/catch blocks, gives you consistent error shapes across all endpoints, and makes it easy to add logging, monitoring, or Sentry integration in one place.`,
      model: 'claude-opus-4.6',
      provider: 'anthropic',
      promptTokens: null,
      completionTokens: 3_820,
      cost: 0.52,
      timestamp: new Date(convBase.getTime() + 45_000).toISOString(),
    },
    {
      id: 'msg_003',
      role: 'human',
      content: `Nice, that's clean. Can you also add rate limiting to this pattern? I want to protect the endpoints.`,
      model: null,
      provider: 'anthropic',
      promptTokens: 980,
      completionTokens: null,
      cost: null,
      timestamp: new Date(convBase.getTime() + 5 * 60_000).toISOString(),
    },
    {
      id: 'msg_004',
      role: 'assistant',
      content: `Absolutely. Here's how to compose rate limiting into the same middleware pattern:

\`\`\`typescript
// middleware/rate-limit.ts
import { Ratelimit } from '@upstash/ratelimit';
import { Redis } from '@upstash/redis';

const ratelimit = new Ratelimit({
  redis: Redis.fromEnv(),
  limiter: Ratelimit.slidingWindow(60, '1 m'), // 60 req/min
});

export function withRateLimit(handler: RouteHandler) {
  return withErrorHandler(async (req: Request, ctx: RouteContext) => {
    const ip = req.headers.get('x-forwarded-for') ?? '127.0.0.1';
    const { success, remaining } = await ratelimit.limit(ip);

    if (!success) {
      throw new AppError(429, 'RATE_LIMITED', 'Too many requests');
    }

    const response = await handler(req, ctx);
    response.headers.set('X-RateLimit-Remaining', remaining.toString());
    return response;
  });
}
\`\`\`

\`\`\`typescript
// Usage — composable middleware
export const GET = withRateLimit(async (req, ctx) => {
  const user = await db.getUser(id);
  if (!user) throw new NotFoundError('User');
  return Response.json(user);
});
\`\`\`

The key insight: \`withRateLimit\` wraps \`withErrorHandler\` internally, so you get both error handling and rate limiting from a single wrapper. You could extend this pattern further with auth, logging, etc.`,
      model: 'claude-opus-4.6',
      provider: 'anthropic',
      promptTokens: null,
      completionTokens: 2_380,
      cost: 0.32,
      timestamp: new Date(convBase.getTime() + 6 * 60_000).toISOString(),
    },
  ],
  promptScore: 62,
  coachingInsights: {
    overall: 62,
    dimensions: [
      { label: 'Clarity', score: 72, tip: 'Good intent, but "make it better" is subjective. Specify what "better" means: fewer lines, type safety, testability?' },
      { label: 'Specificity', score: 48, tip: 'No mention of framework, language, or current error patterns. The model had to guess your stack.' },
      { label: 'Structure', score: 58, tip: 'A flat paragraph. Break into: current state, desired outcome, and constraints.' },
      { label: 'Efficiency', score: 65, tip: 'The prompt is short but too vague, causing a generic response. A few more tokens of context would save a follow-up round.' },
      { label: 'Context', score: 68, tip: 'Mentions "duplicated across routes" but does not show an example of the current code.' },
    ],
    suggestions: [
      'Include a short code snippet showing the current pattern you want to replace',
      'Specify the framework (Next.js, Express, etc.) and TypeScript version',
      'Define "better" with measurable criteria: e.g. "centralized, type-safe, with consistent JSON error shapes"',
      'Mention constraints like backward compatibility or specific error codes your frontend expects',
    ],
    strengths: [
      'Clear high-level task: "refactor error handling"',
      'Identifies the core problem: duplication across routes',
    ],
    rewrite: 'Refactor our Next.js API route error handling (TypeScript, App Router). Currently every route has its own try/catch returning ad-hoc error JSON. I want: (1) a centralized error handler middleware, (2) custom error classes with typed codes like NOT_FOUND and VALIDATION_ERROR, (3) consistent JSON error shape { error: { code, message } }. Here\'s an example of the current pattern:\n\n```ts\nexport async function GET() {\n  try { ... } catch (e) {\n    return Response.json({ error: "Server error" }, { status: 500 });\n  }\n}\n```\n\nKeep backward compatibility with the existing { error: string } shape our frontend expects during migration.',
  },
};
